
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, User, ArrowRight, Lock, Eye, EyeOff, Sparkles, ShieldAlert, Fingerprint, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose }) => {
  const [mode, setMode] = useState<'login' | 'register' | 'admin'>('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const { login, register } = useApp();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      if (mode === 'register') {
        if (!name || !email || !password) {
          setError('All fields are required for guest registration');
          setIsLoading(false);
          return;
        }
        const res = await register(name, email, password);
        if (res.success) {
          onClose();
        } else {
          setError(res.message);
        }
      } else {
        if (!email || !password) {
          setError('Email and password are required');
          setIsLoading(false);
          return;
        }
        const res = await login(email, password);
        if (res.success) {
          onClose();
        } else {
          setError(res.message);
        }
      }
    } catch (err) {
      setError('A system error occurred during authentication.');
    } finally {
      setIsLoading(false);
    }
  };

  const getTitle = () => {
    if (mode === 'admin') return 'Sovereign Portal';
    if (mode === 'register') return 'Guest Registration';
    return 'Contributor Login';
  };

  const getSubtitle = () => {
    if (mode === 'admin') return 'Secure management access for CA Manthan Paliwal.';
    if (mode === 'register') return 'Join the institutional knowledge hub.';
    return 'Access your analysis dashboard.';
  };

  const fillAdminCreds = () => {
    setEmail('manthan@ca.com');
    setPassword('admin123');
    setMode('login');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-md"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="relative w-full max-w-md bg-white border border-slate-200 rounded-[3rem] p-8 md:p-12 shadow-2xl overflow-hidden"
          >
            <div className={`absolute -top-24 -right-24 w-48 h-48 ${mode === 'admin' ? 'bg-red-100' : 'bg-indigo-100'} blur-[80px] rounded-full pointer-events-none transition-colors duration-500`} />
            
            <button 
              onClick={onClose}
              className="absolute top-8 right-8 text-slate-400 hover:text-slate-900 transition-colors z-20"
            >
              <X size={24} />
            </button>

            <div className="text-center mb-10 relative z-10">
              <div className={`w-16 h-16 rounded-2xl ${mode === 'admin' ? 'bg-white border border-red-200 text-red-500' : 'bg-indigo-600 text-white'} flex items-center justify-center mx-auto mb-6 shadow-xl ring-4 ${mode === 'admin' ? 'ring-red-50' : 'ring-indigo-50'} transition-all duration-500`}>
                {mode === 'admin' ? <ShieldAlert size={32} /> : <User size={32} />}
              </div>
              <h2 className="text-3xl font-display font-bold text-slate-900 mb-2">
                {getTitle()}
              </h2>
              <p className="text-slate-500 text-sm">
                {getSubtitle()}
              </p>
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-xs font-bold text-center"
              >
                {error}
              </motion.div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
              {mode === 'register' && (
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. John Doe"
                      className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-50 outline-none transition-all placeholder:text-slate-400"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Work Email</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={mode === 'admin' ? 'manthan@ca.com' : 'name@email.com'}
                    className="w-full pl-12 pr-4 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-50 outline-none transition-all placeholder:text-slate-400"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest ml-1">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-12 pr-12 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-50 outline-none transition-all placeholder:text-slate-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600 transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-4 mt-4 ${mode === 'admin' ? 'bg-white border border-red-200 text-red-600 hover:bg-red-50 shadow-lg shadow-red-100' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-xl shadow-indigo-600/30'} font-bold rounded-2xl flex items-center justify-center gap-2 group transition-all transform active:scale-[0.98] disabled:opacity-70`}
              >
                {isLoading ? <Loader2 size={18} className="animate-spin" /> : (
                  <>
                    {mode === 'login' || mode === 'admin' ? 'Initialize Session' : 'Create Contributor Account'}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 flex flex-col items-center gap-5 relative z-10">
              <button
                onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
                className="text-sm font-bold text-slate-500 hover:text-indigo-600 transition-colors"
              >
                {mode === 'login' ? (
                  <>Don't have an account? <span className="text-indigo-600 underline decoration-indigo-200 underline-offset-4">Register</span></>
                ) : (
                  <>Already registered? <span className="text-indigo-600 underline decoration-indigo-200 underline-offset-4">Sign In</span></>
                )}
              </button>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 w-full">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3 text-center">Institutional Credentials</p>
                <div className="flex flex-col gap-2">
                  <button 
                    onClick={fillAdminCreds}
                    className="flex items-center justify-between px-4 py-2 bg-white rounded-xl border border-slate-200 text-[10px] font-bold text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-all shadow-sm"
                  >
                    <span>Admin: manthan@ca.com</span>
                    <span className="text-indigo-600 uppercase tracking-tighter">Use</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-4 text-center justify-center relative z-10 opacity-30">
              <div className="h-px bg-slate-300 flex-grow" />
              <Fingerprint size={16} className="text-slate-400" />
              <div className="h-px bg-slate-300 flex-grow" />
            </div>

            <p className="mt-8 text-center text-[10px] text-slate-400 leading-relaxed uppercase tracking-[0.3em] font-black">
              Regulatory Infrastructure
            </p>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AuthModal;
